// Shared formatting for the image-quality panels (#1477). Returns an ordered list
// of [label, value] pairs for the metrics that are present on a file's
// quality_metrics; each caller renders its own table rows. Metrics are ordered
// most-relevant-first, and rows are omitted when the underlying field is absent
// (older frames processed before a metric existed simply show fewer rows).

const isNum = (v) => v != null && Number.isFinite(v);

export function buildQualityRows(q) {
  if (!q) return [];
  const rows = [];

  // FWHM — seeing / focus, the primary image-quality indicator (arcsec + pixels).
  if (isNum(q.fwhm_arcsec) || isNum(q.fwhm_pixels)) {
    const parts = [];
    if (isNum(q.fwhm_arcsec)) parts.push(`${q.fwhm_arcsec.toFixed(2)}"`);
    if (isNum(q.fwhm_pixels)) parts.push(`(${q.fwhm_pixels.toFixed(2)} px)`);
    rows.push(['FWHM', parts.join(' ')]);
  }

  // Limiting magnitude (5-sigma depth) — only the catalog-calibrated value, which
  // is a real on-sky magnitude. The instrumental value (limiting_mag_instrumental)
  // is relative to an arbitrary zero-point (typically negative) and meaningless to
  // a user, so it is intentionally NOT shown; the row is simply omitted for frames
  // that couldn't be calibrated (narrowband, or no catalog cross-match).
  if (isNum(q.limiting_mag)) {
    rows.push(['Limiting mag (5σ)', q.limiting_mag.toFixed(2)]);
  }

  // Stars detected.
  if (isNum(q.num_stars_detected)) {
    rows.push(['Stars detected', String(q.num_stars_detected)]);
  }

  // Signal-to-noise — prefer the photometric per-star SNR; fall back to the legacy
  // peak value (only that exists on frames processed before the photometric SNR).
  if (isNum(q.snr_photometric_median)) {
    rows.push(['Signal-to-noise', `${q.snr_photometric_median.toFixed(1)} (photometric)`]);
  } else if (isNum(q.snr)) {
    rows.push(['Signal-to-noise', `${q.snr.toFixed(1)} (peak)`]);
  }

  // Photometric zero-point (MAGZERO) — only when a catalog match was made.
  if (isNum(q.photometric_zeropoint)) {
    rows.push(['Zero-point (MAGZERO)', q.photometric_zeropoint.toFixed(3)]);
  }

  // Background sky level (+/- noise RMS).
  if (isNum(q.background_level)) {
    rows.push(['Background', isNum(q.background_noise)
      ? `${q.background_level.toFixed(1)} ± ${q.background_noise.toFixed(1)}`
      : q.background_level.toFixed(1)]);
  }

  // Pixel range (ADU) — kept as a saturation/clipping indicator (max near the
  // full-well/ADC ceiling flags saturated frames). Mean/median/std are dropped:
  // raw ADU averages duplicate the background level and carry no quality signal.
  if (isNum(q.min) && isNum(q.max)) {
    rows.push(['Min / Max (ADU)', `${q.min.toFixed(0)} / ${q.max.toFixed(0)}`]);
  }

  return rows;
}
